import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * CloudinaryUpload - Componente para subir imágenes a Cloudinary
 * 
 * Props:
 * - value: URL actual de la imagen
 * - onChange: Callback cuando se sube una imagen (recibe la URL)
 * - folder: Carpeta en Cloudinary (default: "escuela/general")
 * - className: Clases CSS adicionales
 * - aspectRatio: Relación de aspecto del preview ("square", "video", "portrait")
 * - placeholder: Texto del placeholder
 */
const CloudinaryUpload = ({ 
  value = "", 
  onChange, 
  folder = "escuela/general",
  className = "",
  aspectRatio = "video",
  placeholder = "Arrastra una imagen o haz clic para seleccionar"
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const aspectClasses = {
    square: "aspect-square",
    video: "aspect-video",
    portrait: "aspect-[3/4]"
  };

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar 10MB');
      return;
    }

    setUploading(true);

    try {
      // Get signature from backend
      const sigResponse = await api.get(`/cloudinary/signature?folder=${folder}&resource_type=image`);
      const sig = sigResponse.data;

      // Upload directly to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      const result = await uploadResponse.json();

      if (result.secure_url) {
        onChange(result.secure_url);
        toast.success('Imagen subida correctamente');
      } else {
        throw new Error(result.error?.message || 'Error al subir imagen');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleRemove = () => {
    onChange("");
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {value ? (
        // Preview de imagen
        <div className={`relative rounded-xl overflow-hidden bg-[hsl(var(--sage-light))] ${aspectClasses[aspectRatio]}`}>
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-8 w-8 p-0 bg-white/90 hover:bg-white shadow-sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-8 w-8 p-0 bg-white/90 hover:bg-red-50 text-red-600 shadow-sm"
              onClick={handleRemove}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        // Zona de drop
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative rounded-xl border-2 border-dashed cursor-pointer
            transition-all duration-200
            ${aspectClasses[aspectRatio]}
            ${dragOver 
              ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' 
              : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50 bg-[hsl(var(--sage-light))]'
            }
          `}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
            {uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-[hsl(var(--accent))] animate-spin mb-2" />
                <p className="text-sm text-[hsl(var(--text-secondary))]">Subiendo imagen...</p>
              </>
            ) : (
              <>
                <ImageIcon className="w-8 h-8 text-[hsl(var(--text-tertiary))] mb-2" />
                <p className="text-sm text-[hsl(var(--text-secondary))]">{placeholder}</p>
                <p className="text-xs text-[hsl(var(--text-tertiary))] mt-1">JPG, PNG, WebP hasta 10MB</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudinaryUpload;
